'use client'

import { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useSession }  from 'next-auth/react'
import AppShell      from '../components/layout/AppShell'
import LandingView   from '../components/landing/LandingView'
import { QuantumLoader } from '@/components/ui/QuantumLoader'
import { useProfile }        from '@/hooks/landing/useProfile'
import { useNews }           from '@/hooks/landing/useNews'
import { useOnlinePresence } from '@/hooks/landing/useOnlinePresence'
import { useHeartbeat }      from '@/hooks/landing/useHeartbeat'
import { useKonami }         from '@/hooks/landing/useKonami'
import dynamic from 'next/dynamic'
import { QuizCard }          from '@/components/landing/QuizCard'
import { HumanMissionsCard } from '@/components/landing/HumanMissionsCard'

// Carte interactive (MapLibre) — lourde, chargée côté client uniquement
const CartePanel = dynamic(() => import('./(dashboard)/lawyer/page').then(m => m.CartePanel), {
  ssr: false,
  loading: () => <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>Chargement de la carte…</div>,
})
import { TutorialCard }      from '@/components/landing/TutorialCard'
import { SectionLabel }      from '@/components/landing/SectionLabel'
import { HeroBanner }        from '@/components/landing/HeroBanner'
import { DocsSection }       from '@/components/admin/DocsSection'
import { MissionControl }    from '@/components/command/MissionControl'
import { HomeSearchCard }    from '@/components/landing/HomeSearchCard'
import { HomeLegalStats }    from '@/components/landing/HomeLegalStats'
import { WorkStartCard }     from '@/components/landing/WorkStartCard'
import { HomeDashboard }     from '@/components/landing/HomeDashboard'
import type { UserProfile }  from '@/types/landing'


// ─── Page principale ──────────────────────────────────────────────────────────
export default function LandingPage() {
  const { data: session, status: sessionStatus } = useSession()
  const searchParams = useSearchParams()

  // Hooks extraits (voir apps/web/src/hooks/landing/*)
  const { profile, loading: profileLoading } = useProfile(sessionStatus)
  const isAdmin = profile?.role === 'ADMIN' || profile?.role === 'SUPER_ADMIN'

  const [region, setRegion] = useState('15')
  const [tutorialDismissed, setTutorialDismissed] = useState(true)
  // Accueil public : une fois entré (?os=1 ou déjà visité), on ne revoit plus
  // la landing — même sans session. /parametres et la création dans /projets
  // restent gatées ailleurs (Sidebar, projets/page.tsx).
  const [enteredOS, setEnteredOS] = useState(false)

  const { news } = useNews(region)
  const onlineInfo = useOnlinePresence()
  const eggVisible = useKonami()
  useHeartbeat(sessionStatus, region)

  // Région + tutoriel depuis localStorage (état local, léger)
  useEffect(() => {
    const saved = localStorage.getItem('ab_region')
    if (saved) setRegion(saved)
    const dismissed = localStorage.getItem('ab_tutorial_dismissed')
    if (!dismissed) setTutorialDismissed(false)
  }, [])

  useEffect(() => {
    if (searchParams.get('os') === '1') localStorage.setItem('ab_enter_os', '1')
    setEnteredOS(localStorage.getItem('ab_enter_os') === '1')
  }, [searchParams])

  const dismissTutorial = () => {
    setTutorialDismissed(true)
    localStorage.setItem('ab_tutorial_dismissed', '1')
  }

  if (sessionStatus === 'loading' || profileLoading) {
    return <QuantumLoader label="Amiral Blanc — synchronisation…" />
  }

  // Visiteurs non-authentifiés qui n'ont jamais franchi la porte → landing immersive
  if (sessionStatus !== 'authenticated' && !enteredOS) {
    return <LandingView />
  }

  const firstName = profile?.name?.split(' ')[0] ?? session?.user?.name?.split(' ')[0] ?? null

  // Format unifié : le dashboard authentifié vit dans l'AppShell (même navbar/
  // Sidebar/Topbar/Footer que le reste de l'app). Le body est le centre du shell.
  return (
    <AppShell>
      <div style={{ padding: '24px 28px', animation: 'fadeUp 0.4s var(--ease-out)' }}>

        {/* Easter egg — Konami */}
        {eggVisible && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}>
            <div style={{
              background: 'var(--bg-card)',
              border: '2px solid var(--accent)',
              borderRadius: 16, padding: '32px 48px',
              textAlign: 'center', animation: 'fadeUp 0.3s ease-out',
              boxShadow: '0 0 80px rgba(79,124,255,0.3)',
            }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}><span style={{ fontSize: '18px', color: '#4f7cff' }}>
  🦋
</span></div>
              <div className="font-display" style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>
                Code secret déverrouillé !
              </div>
              <div style={{ fontSize: 13, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
                Salut! Tu viens de trouver l'easter egg d'Amiral Blanc.<br />
                +100 XP ajoutés à ton profil secret 🫡
              </div>
            </div>
          </div>
        )}

        {/* Accueil holographique : « Salutations {usager} » + résumé, puis les cartes alimentées par de vraies requêtes */}
        <HomeDashboard firstName={firstName} userId={(session?.user as { id?: string } | undefined)?.id} />

        {/* Niveau/XP (HeroBanner) — conservé, sous le tableau de bord */}
        <HeroBanner firstName={firstName} onlineInfo={onlineInfo} profile={profile} />

        {/* Environnement de travail — projets, CRM, messages, agents (lib/presentation/modules.ts) */}
        <WorkStartCard />

        {/* Tutoriel — carte dismissable */}
        {!tutorialDismissed && (
          <TutorialCard onDismiss={dismissTutorial} />
        )}

        {/* Recherche IA + Challenge — 50/50 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          <section style={{ minWidth: 0 }}>
            <SectionLabel>Recherche</SectionLabel>
            <HomeSearchCard />
          </section>
          <section style={{ minWidth: 0 }}>
            <SectionLabel>Challenge</SectionLabel>
            {/* Missions humaines TOUJOURS visibles (état vide inclus) —
                le challenge-jeu (quiz) empilé dessous */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <HumanMissionsCard />
              <QuizCard />
            </div>
          </section>
        </div>

        {/* Command Center — Mission Control (remonté : visible sans scroller) */}
        <MissionControl news={news} />

        {/* Legal Engine — stats, santé des villes, jobs (boutons toggle) */}
        <section style={{ marginBottom: 48 }}>
          <HomeLegalStats />
        </section>

        {/* Carte du territoire — zonage, parcelles, règlements géolocalisés */}
        <section style={{ marginBottom: 48 }}>
          <SectionLabel>Carte du territoire</SectionLabel>
          <div className="hud-card" style={{ padding: '14px 16px', minWidth: 0 }}>
            <CartePanel />
            <a href="/technologue/carte" style={{ display: 'inline-block', marginTop: 8, fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textDecoration: 'none' }}>
              Carte interactive complète →
            </a>
          </div>
        </section>

        {/* Admin : Documentation */}
        {isAdmin && <DocsSection />}
      </div>
    </AppShell>
  )
}





